import { saveAgencyCredentials, removeAgencyCredentials } from './automationService';

export interface CarrierCredentials {
  username: string;
  password: string;
}

export interface CarrierConnectionState {
  carrierId: string;
  connected: boolean;
  username?: string;
  enabledActions: string[];
  connectedAt?: string;
}

const STORAGE_KEY = 'carrierAccessConnections';

function readLocalState(): Record<string, CarrierConnectionState> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeLocalState(state: Record<string, CarrierConnectionState>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getConnectionStates(): Record<string, CarrierConnectionState> {
  return readLocalState();
}

/**
 * Saves this carrier's portal login for the signed-in agency to the local
 * automation server (see /agency-credentials in mcp-server/server.mjs),
 * keyed by agencyId (the NCAgencyId set on sign-in) — NOT the central
 * momentumampapi backend, which has no carrier-credentials endpoint yet.
 * `persisted: true` means the automation server actually accepted it (so a
 * job run for this agency will use it); the local connection/username is
 * always tracked here too so the UI keeps working even if the automation
 * server is unreachable.
 */
export async function saveCarrierCredentials(
  carrierId: string,
  credentials: CarrierCredentials,
  agencyId: string,
): Promise<{ persisted: boolean }> {
  let persisted = false;
  try {
    await saveAgencyCredentials(agencyId, carrierId, credentials.username, credentials.password);
    persisted = true;
  } catch {
    persisted = false;
  }

  const state = readLocalState();
  state[carrierId] = {
    carrierId,
    connected: true,
    username: credentials.username,
    enabledActions: state[carrierId]?.enabledActions ?? [],
    connectedAt: state[carrierId]?.connectedAt ?? new Date().toISOString(),
  };
  writeLocalState(state);

  return { persisted };
}

export function setActionEnabled(carrierId: string, actionKey: string, enabled: boolean): void {
  const state = readLocalState();
  // A carrier connected via real server-verified credentials (see
  // getCredentialsStatus) may have no local entry yet — enabledActions is
  // still tracked locally regardless of how the connection itself became
  // "true", so create one rather than silently no-op.
  const existing = state[carrierId] ?? { carrierId, connected: true, enabledActions: [] };
  const enabledActions = new Set(existing.enabledActions);
  if (enabled) {
    enabledActions.add(actionKey);
  } else {
    enabledActions.delete(actionKey);
  }
  state[carrierId] = { ...existing, enabledActions: Array.from(enabledActions) };
  writeLocalState(state);
}

export function disconnectCarrier(carrierId: string, agencyId?: string): void {
  const state = readLocalState();
  delete state[carrierId];
  writeLocalState(state);
  if (agencyId) {
    removeAgencyCredentials(agencyId, carrierId).catch(() => {});
  }
}

const HEADED_STORAGE_KEY = 'carrierAccessHeaded';

function readHeadedState(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(HEADED_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Carrier-level preference (not per-quote): when true, Create Quote runs
 * in a visible browser and leaves it open at the end — success or stuck —
 * so the agency can take over and finish (including clicking Bind, which
 * the automation itself never does). Set once per carrier in its detail
 * panel, applied to every quote run from then on.
 */
export function getHeadedPreferences(): Record<string, boolean> {
  return readHeadedState();
}

export function setHeadedPreference(carrierId: string, headed: boolean): void {
  const state = readHeadedState();
  if (headed) {
    state[carrierId] = true;
  } else {
    delete state[carrierId];
  }
  localStorage.setItem(HEADED_STORAGE_KEY, JSON.stringify(state));
}
